import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { Bot, CreditCard, Sparkles, Zap } from 'lucide-react-native';
import {
  Platform,
  ScrollView,
  StyleSheet,
  type TextStyle,
  useWindowDimensions,
  View,
} from 'react-native';
import { useTranslation } from '../../contexts/TranslationContext';
import { trackEvent } from '../../lib/analytics';
import { useMixpanel } from '../../lib/mixpanel';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../theme/typography';
import { FadeIn } from '../animations';
import { Badge } from '../primitives/Badge';
import { Button } from '../primitives/Button';
import { Container } from '../primitives/Container';
import { Text } from '../primitives/Text';
import { GlowBloom } from './landingStyleKit';

const AVATARS = [
  { id: 'a1', initials: 'ML', color: colors.primary[500] },
  { id: 'a2', initials: 'ER', color: colors.accent.blue[400] },
  { id: 'a3', initials: 'SK', color: colors.accent.purple[400] },
  { id: 'a4', initials: 'AP', color: colors.status.success.main },
];

const TRUST_BADGES = [
  { id: 'founding', icon: Sparkles },
  { id: 'noCard', icon: CreditCard },
  { id: 'earlyAccess', icon: Zap },
] as const;

type BadgeColor = 'cyan' | 'green' | 'red' | 'amber' | 'purple' | 'blue' | 'neutral';

interface BoardLead {
  initials: string;
  name: string;
  company: string;
  value: string;
  tag: string;
}

const BOARD: {
  key: string;
  accent: string;
  tagColor: BadgeColor;
  leads: BoardLead[];
}[] = [
  {
    key: 'newLead',
    accent: colors.text.tertiary,
    tagColor: 'neutral',
    leads: [
      {
        initials: 'ML',
        name: 'Marcus Lee',
        company: 'Northwind Co',
        value: '$4.2k',
        tag: 'AI captured',
      },
      {
        initials: 'PS',
        name: 'Priya Shah',
        company: 'Lumen Labs',
        value: '$2.8k',
        tag: 'AI captured',
      },
    ],
  },
  {
    key: 'discovery',
    accent: colors.accent.blue[400],
    tagColor: 'blue',
    leads: [
      {
        initials: 'ER',
        name: 'Elena Ruiz',
        company: 'Vertex AI',
        value: '$9.5k',
        tag: 'AI qualified',
      },
    ],
  },
  {
    key: 'proposal',
    accent: colors.accent.purple[400],
    tagColor: 'purple',
    leads: [
      {
        initials: 'SK',
        name: 'Sara Kim',
        company: 'Brightpath',
        value: '$18k',
        tag: 'Meeting booked',
      },
    ],
  },
  {
    key: 'closedWon',
    accent: colors.status.success.main,
    tagColor: 'green',
    leads: [
      { initials: 'AP', name: 'Anya Patel', company: 'Helios', value: '$24k', tag: 'Human close' },
    ],
  },
];

const LeadMiniCard = ({
  lead,
  accent,
  tagColor,
}: {
  lead: BoardLead;
  accent: string;
  tagColor: BadgeColor;
}) => (
  <View style={[boardStyles.leadCard, { borderLeftColor: accent }]}>
    <View style={boardStyles.leadHead}>
      <View style={[boardStyles.leadAvatar, { backgroundColor: accent }]}>
        <Text style={boardStyles.leadAvatarText}>{lead.initials}</Text>
      </View>
      <View style={boardStyles.leadIdentity}>
        <Text variant="bodySmall" numberOfLines={1} style={boardStyles.leadName}>
          {lead.name}
        </Text>
        <Text variant="caption" numberOfLines={1} color={colors.text.tertiary}>
          {lead.company}
        </Text>
      </View>
    </View>
    <View style={boardStyles.leadFoot}>
      <Badge
        color={tagColor}
        size="sm"
        icon={<Bot size={11} color={colors.primary[400]} strokeWidth={2} />}
      >
        {lead.tag}
      </Badge>
      <Text style={boardStyles.leadValue}>{lead.value}</Text>
    </View>
  </View>
);

const FunnelMock = () => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isNarrow = width < tokens.breakpoints.tablet;

  const columns = BOARD.map((col) => (
    <View key={col.key} style={[boardStyles.column, isNarrow && boardStyles.columnNarrow]}>
      <View style={boardStyles.columnHead}>
        <View style={boardStyles.columnLabel}>
          <View style={[boardStyles.columnDot, { backgroundColor: col.accent }]} />
          <Text variant="bodySmall" style={boardStyles.columnTitle}>
            {t(`landing.product.board.stages.${col.key}`)}
          </Text>
        </View>
        <Text style={boardStyles.columnCount}>{col.leads.length}</Text>
      </View>
      {col.leads.map((lead) => (
        <LeadMiniCard key={lead.name} lead={lead} accent={col.accent} tagColor={col.tagColor} />
      ))}
    </View>
  ));

  return (
    <View style={boardStyles.board}>
      <View style={boardStyles.chrome}>
        <View style={boardStyles.dots}>
          <View style={[boardStyles.dot, { backgroundColor: colors.status.error.main }]} />
          <View style={[boardStyles.dot, { backgroundColor: colors.status.warning.main }]} />
          <View style={[boardStyles.dot, { backgroundColor: colors.status.success.main }]} />
        </View>
        <Text style={boardStyles.urlBar}>flowsellai.com/funnel</Text>
        <Badge color="green" size="sm">
          {t('landing.product.board.live')}
        </Badge>
      </View>
      {isNarrow ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={boardStyles.columnsScroll}
        >
          {columns}
        </ScrollView>
      ) : (
        <View style={boardStyles.columns}>{columns}</View>
      )}
    </View>
  );
};

// The 68px display headline fits English on web, but long es/pt words
// ("vendedores", "trabalha") overflow narrow viewports — scale it down by width.
const responsiveHeroFont = (width: number): TextStyle => {
  if (Platform.OS !== 'web') return { fontSize: 38, lineHeight: 46, letterSpacing: -1 };
  if (width >= tokens.breakpoints.desktop)
    return { fontSize: 68, lineHeight: 72, letterSpacing: -2 };
  if (width >= tokens.breakpoints.tablet)
    return { fontSize: 52, lineHeight: 56, letterSpacing: -1.5 };
  if (width >= 480) return { fontSize: 42, lineHeight: 46, letterSpacing: -1 };
  return { fontSize: 32, lineHeight: 36, letterSpacing: -0.5 };
};

/**
 * Where the signup CTAs lead. Defaults to signup so this stays a pure
 * presentational component: the landing screens own the capacity lookup, and an
 * unanswered gate must never render a waitlist it has not confirmed.
 */
export interface AccessGateCta {
  destination?: '/login' | '/waitlist';
}

export const PremiumHeroSection = ({ destination = '/login' }: AccessGateCta = {}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const mp = useMixpanel();
  const { width } = useWindowDimensions();
  const heroFont = responsiveHeroFont(width);

  const handleGetStarted = () => {
    trackEvent(mp, 'landing.login_cta_clicked', { source: 'hero', destination });
    router.push(destination);
  };

  return (
    <View style={styles.wrapper}>
      <Container maxWidth="xl" style={styles.container}>
        <View style={styles.content}>
          <FadeIn delay={0} translateY={12}>
            <View style={styles.social}>
              <View style={styles.avatars}>
                {AVATARS.map((avatar, index) => (
                  <View
                    key={avatar.id}
                    style={[
                      styles.avatar,
                      { backgroundColor: avatar.color, marginLeft: index === 0 ? 0 : -10 },
                    ]}
                  >
                    <Text style={styles.avatarText}>{avatar.initials}</Text>
                  </View>
                ))}
              </View>
              <Text variant="bodySmall" color={colors.text.secondary}>
                {t('landing.hero.social')}
              </Text>
            </View>
          </FadeIn>

          <FadeIn delay={80} translateY={16}>
            <View style={styles.headline} accessibilityRole="header" aria-level={1}>
              <Text style={[styles.headlineText, heroFont]}>{t('landing.hero.preTitle')}</Text>
              <Text style={[styles.headlineText, heroFont, styles.headlineAccent]}>
                {t('landing.hero.mainTitle')}
              </Text>
            </View>
          </FadeIn>

          <FadeIn delay={160} translateY={16}>
            <Text
              variant="bodyLarge"
              color={colors.text.secondary}
              align="center"
              style={styles.subtitle}
            >
              {t('landing.hero.subtitle')}
            </Text>
          </FadeIn>

          <FadeIn delay={220} translateY={14}>
            <View style={styles.trustRow}>
              {TRUST_BADGES.map(({ id, icon: Icon }) => (
                <View key={id} style={styles.trustItem}>
                  <Icon size={15} color={colors.primary[400]} strokeWidth={2} />
                  <Text variant="bodySmall" color={colors.text.secondary}>
                    {t(`landing.hero.trust.${id}`)}
                  </Text>
                </View>
              ))}
            </View>
          </FadeIn>

          <FadeIn delay={300} translateY={14}>
            <View style={styles.ctaBlock}>
              <Button
                testID="landing_hero_login_cta"
                variant="primary"
                size="lg"
                pill
                style={styles.ctaButton}
                onPress={handleGetStarted}
              >
                {t('landing.hero.primaryCta')}
              </Button>
              <Text variant="caption" color={colors.text.tertiary} style={styles.ctaNote}>
                {t('landing.hero.ctaNote')}
              </Text>
            </View>
          </FadeIn>
        </View>

        <FadeIn delay={380} translateY={24}>
          <View style={styles.mockWrap}>
            <GlowBloom size={420} style={styles.mockGlow} />
            <FunnelMock />
            <LinearGradient
              colors={['transparent', colors.background.primary]}
              style={styles.mockFade}
              pointerEvents="none"
            />
          </View>
        </FadeIn>
      </Container>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    backgroundColor: colors.transparent,
    paddingTop: Platform.select({ web: 132, native: 96 }),
    paddingBottom: Platform.select({ web: 56, native: 48 }),
    overflow: 'hidden',
  },
  container: {
    width: '100%',
  },
  headline: {
    alignItems: 'center',
    maxWidth: 860,
  },
  headlineText: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: Platform.select({ web: 68, default: 38 }),
    lineHeight: Platform.select({ web: 71, default: 40 }),
    fontWeight: '800',
    letterSpacing: Platform.select({ web: -2, default: -1 }),
    textAlign: 'center',
    color: colors.text.primary,
  },
  headlineAccent: Platform.select({
    web: {
      color: 'transparent',
      backgroundImage: colors.gradients.brandText,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    } as unknown as TextStyle,
    default: { color: colors.primary.neon },
  }),
  subtitle: {
    maxWidth: 600,
  },
  content: {
    alignItems: 'center',
    gap: spacing.xl,
    paddingHorizontal: spacing.md,
  },
  social: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.border.muted,
    backgroundColor: colors.surface.row,
  },
  avatars: {
    flexDirection: 'row',
  },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 2,
    borderColor: colors.background.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.text.inverse,
  },
  trustRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.lg,
  },
  trustItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  ctaBlock: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  ctaButton: {
    minWidth: Platform.select({ web: 220, native: 200 }),
  },
  ctaNote: {
    textAlign: 'center',
  },
  mockWrap: {
    width: '100%',
    maxWidth: 1080,
    alignSelf: 'center',
    marginTop: Platform.select({ web: 56, default: 36 }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  mockGlow: {
    top: 24,
  },
  mockFade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 120,
  },
});

const boardStyles = StyleSheet.create({
  board: {
    width: '100%',
    borderRadius: tokens.radius.xl,
    borderWidth: 1,
    borderColor: colors.border.muted,
    backgroundColor: colors.surface.cardOverlay,
    overflow: 'hidden',
    ...(tokens.shadow?.lg ?? {}),
  },
  chrome: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.muted,
    backgroundColor: colors.background.secondary,
  },
  dots: {
    flexDirection: 'row',
    gap: 6,
  },
  dot: {
    width: 11,
    height: 11,
    borderRadius: 6,
  },
  urlBar: {
    flex: 1,
    textAlign: 'center',
    fontFamily: darkPrecisionFontFamilies.metric,
    fontSize: 12,
    color: colors.text.tertiary,
  },
  columns: {
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.lg,
  },
  columnsScroll: {
    flexDirection: 'row',
    gap: spacing.md,
    padding: spacing.lg,
  },
  column: {
    flex: 1,
    minWidth: 0,
    gap: spacing.sm,
  },
  columnNarrow: {
    width: 180,
    flex: undefined,
  },
  columnHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  columnLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  columnDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  columnTitle: {
    fontWeight: '600',
    color: colors.text.secondary,
  },
  columnCount: {
    fontFamily: darkPrecisionFontFamilies.metric,
    fontSize: 11,
    color: colors.text.tertiary,
  },
  leadCard: {
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.border.muted,
    backgroundColor: colors.surface.row,
    padding: spacing.md,
    gap: spacing.md,
  },
  leadHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  leadAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  leadAvatarText: {
    fontSize: 10,
    fontWeight: '700',
    color: colors.text.inverse,
  },
  leadIdentity: {
    flex: 1,
    minWidth: 0,
  },
  leadName: {
    fontWeight: '600',
    color: colors.text.primary,
  },
  leadFoot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border.subtle,
  },
  leadValue: {
    fontFamily: darkPrecisionFontFamilies.metricBold,
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.primary,
  },
});
